/**
 * InlineCitations — chain-prose renderer with word-level karaoke
 * highlight (Apr 30 2026 v3).
 *
 * Lead's brief in this iteration:
 *   • "Text needs to be bigger. And brighter your killing my eyes."
 *   • "And the beginning its a blob unless you follow text. Be precise."
 *   • "This tech was here in 2008. Its 2026. Very precisely following
 *      my voice and in the future is now way."
 *
 * Resolution:
 *   • Every word is wrapped in a span. The component fetches the
 *     chain's word-level alignment from
 *     /api/narration/chain/<id>/full/alignment and uses
 *     `narrationOffset` + a body-to-word index map to figure out which
 *     displayed word is the one being narrated **right now**.
 *   • Past words sit at white/82 (readable, not faded). Active word is
 *     pure white, gold-glow + larger weight. Upcoming words at white/68
 *     so the eye still tracks forward without blob-effect.
 *   • Whole section highlight: when audio is in this body's voice-char
 *     range, the wrapping section gets a soft gold left-bar and warm
 *     wash. Past sections stay legible at 65% opacity. Upcoming at 50%.
 *   • Cite chips stay inline + underlined gold. When the active word
 *     IS a cite, the link itself pulses a brighter halo.
 *   • Auto-scroll: when activeWordIdx changes inside an actively-narrated
 *     body, smooth-scroll that word to the centre of the viewport.
 */
import { useMemo, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';

const CITATION_RE = /\[(\d+)\]/g;
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=[A-Z"'(])/;

// ─── Author + year extraction for the inline anchor text ──────────
function _extractAuthorYear(source) {
  if (!source) return null;
  const speaker = (source.speaker_name || '').trim();
  const role = (source.speaker_role || '').trim();
  const yearMatch = role.match(/\b(1\d{3}|20\d{2})\b/) || speaker.match(/\b(1\d{3}|20\d{2})\b/);
  const year = yearMatch ? yearMatch[1] : '';
  let author = speaker
    .replace(/\bet\s*al\.?/i, '')
    .replace(/\b&\s+\w+/g, '')
    .replace(/,.+$/, '')
    .trim();
  if (!author) author = speaker;
  return year ? `${author} ${year}` : (author || `Source ${role}`);
}

// ─── Glass-morphism hover popup ───────────────────────────────────
function CitePopup({ source, anchorRect, citeIdx, onClose }) {
  if (!source || !anchorRect) return null;
  const top = anchorRect.bottom + window.scrollY + 8;
  const maxLeft = window.innerWidth - 380;
  const left = Math.max(12, Math.min(anchorRect.left + window.scrollX - 14, maxLeft));
  const journal = source.archive_name || (source.speaker_role || '').replace(/\s*\(\d{4}\)\s*$/, '');
  const yearMatch = (source.speaker_role || '').match(/\b(1\d{3}|20\d{2})\b/);
  const year = yearMatch ? yearMatch[1] : '';
  return (
    <div
      role="tooltip"
      data-testid={`cite-popup-${citeIdx}`}
      onMouseLeave={onClose}
      style={{
        position: 'absolute', top, left,
        width: 'min(94vw, 360px)', zIndex: 80,
        background: 'rgba(10,10,12,0.86)',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        border: '1px solid rgba(212,168,74,0.32)',
        boxShadow: '0 18px 60px -12px rgba(0,0,0,0.85), 0 0 0 1px rgba(47,138,146,0.06) inset',
        animation: 'cite-pop-fade 220ms ease-out forwards',
      }}
      className="rounded-xl overflow-hidden pointer-events-auto"
    >
      <div className="px-5 pt-4 pb-3 border-b border-white/8 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#D4A84A]/85 mb-1.5">Primary source · peer-reviewed</div>
          <div className="font-historical text-white text-base md:text-lg leading-tight">
            {source.title || (source.text ? source.text.slice(0, 120) : 'Source')}
          </div>
        </div>
        <span className="font-historical text-[#D4A84A]/35 text-3xl leading-none select-none">“</span>
      </div>
      <div className="px-5 py-3 space-y-2">
        <div className="flex items-baseline gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
          {source.speaker_name && <span className="text-[#2F8A92]/85">{source.speaker_name}</span>}
          {(source.speaker_name && (journal || year)) && <span className="text-white/25">·</span>}
          {journal && <span className="text-white/65">{journal}</span>}
          {year && (<><span className="text-white/25">·</span><span className="text-[#D4A84A]/85">{year}</span></>)}
        </div>
        {source.text && (
          <p className="font-historical italic text-sm text-white/75 leading-relaxed border-l-2 border-[#D4A84A]/40 pl-3">
            {source.text}
          </p>
        )}
      </div>
      {source.archive_url && (
        <a
          href={source.archive_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`cite-popup-link-${citeIdx}`}
          className="block px-5 py-3 border-t border-white/8 group hover:bg-[#D4A84A]/8 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2F8A92] group-hover:text-[#D4A84A] transition-colors">
              Read on {source.archive_name || 'archive'}
            </span>
            <ExternalLink size={13} className="text-[#2F8A92] group-hover:text-[#D4A84A] group-hover:translate-x-0.5 transition-all" />
          </div>
        </a>
      )}
    </div>
  );
}

// ─── Inline cite link — gold underline + hover popup ─────────────
function CiteLink({ source, index, isActiveWord, testIdPrefix, voiceWordIdx, dataActiveWordIdx }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  if (!source) {
    return <span className="text-white/40 italic">[{index}]</span>;
  }
  const label = _extractAuthorYear(source);
  const show = () => { if (ref.current) { setRect(ref.current.getBoundingClientRect()); setOpen(true); } };
  const hide = () => setOpen(false);
  return (
    <>
      <a
        ref={ref}
        href={source.archive_url || '#'}
        target={source.archive_url ? '_blank' : undefined}
        rel={source.archive_url ? 'noopener noreferrer' : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        data-testid={`${testIdPrefix}-link-${index}`}
        data-voice-word={voiceWordIdx}
        className={`inline transition-colors underline decoration-1 underline-offset-[3px] cursor-pointer ${isActiveWord ? 'text-[#FFE08A] decoration-[#D4A84A] cite-link-narrating' : 'text-[#D4A84A] hover:text-[#2F8A92] decoration-[#D4A84A]/55 hover:decoration-[#2F8A92]'}`}
      >
        {label}
      </a>
      {open && <CitePopup source={source} anchorRect={rect} citeIdx={index} onClose={hide} />}
    </>
  );
}

// ─── Tokenize the displayed body into a flat sequence of word-tokens
//     (text-words + cite-anchors) and whitespace runs preserving the
//     original indentation. Each text word gets a voice-word index
//     by walking the body's char-to-voice-word map. ────────────────
function _tokenize(body, sources, voiceText, wordRanges, voiceCharOffset) {
  if (!body) return { paragraphs: [], total: 0 };
  // Build a body-char → voice-word-idx map by walking the body and
  // matching against voiceText starting at `voiceCharOffset`.
  const bodyLen = body.length;
  const charMap = new Array(bodyLen).fill(-1);
  let v = voiceCharOffset;
  let wordCursor = 0;
  // Advance wordCursor to the first range whose end > voiceCharOffset.
  while (wordCursor < wordRanges.length && wordRanges[wordCursor].end <= v) wordCursor++;
  for (let d = 0; d < bodyLen; d++) {
    const ch = body[d];
    if (ch === '[') {
      const close = body.indexOf(']', d);
      if (close > d) { d = close; continue; }
    }
    if (!/\S/.test(ch)) continue; // ignore whitespace in displayed
    // Skip any whitespace / section-separator chars in voiceText.
    while (v < voiceText.length && /\s/.test(voiceText[v])) v++;
    if (v >= voiceText.length) break;
    if (voiceText[v] !== ch) {
      // Drift recovery: skip ahead in voiceText looking for this char.
      let found = -1;
      for (let k = v; k < Math.min(v + 80, voiceText.length); k++) {
        if (voiceText[k] === ch) { found = k; break; }
      }
      if (found < 0) {
        v++; continue;
      }
      v = found;
    }
    while (wordCursor < wordRanges.length && wordRanges[wordCursor].end <= v) wordCursor++;
    if (wordCursor < wordRanges.length && v >= wordRanges[wordCursor].start && v < wordRanges[wordCursor].end) {
      charMap[d] = wordCursor;
    }
    v++;
  }

  // Split into paragraphs, then sentences, then word-tokens.
  const paragraphs = [];
  const paraSplit = body.split(/\n{2,}/);
  let absoluteOffset = 0;
  let totalTokens = 0;

  paraSplit.forEach((para, pIdx) => {
    const trimmed = para.replace(/^\s+|\s+$/g, '');
    if (!trimmed) { absoluteOffset += para.length + 2; return; }
    const sentences = trimmed.split(SENTENCE_BOUNDARY);
    const paraSentences = [];
    sentences.forEach((sent) => {
      const tokens = [];
      // Walk sent char-by-char, slicing into runs of non-cite text and
      // discrete cite markers. For each run, split into space-delimited
      // words preserving punctuation.
      const startInBody = absoluteOffset + para.indexOf(sent);
      const re = new RegExp(CITATION_RE.source, 'g');
      let lastIdx = 0;
      let mm;
      while ((mm = re.exec(sent)) !== null) {
        if (mm.index > lastIdx) {
          tokens.push(...wordsFromRun(sent.slice(lastIdx, mm.index), startInBody + lastIdx, charMap));
        }
        const idx = parseInt(mm[1], 10);
        const src = (idx >= 1 && idx <= sources.length) ? sources[idx - 1] : null;
        // Voice word index for a cite link = the voice word at the cite
        // marker's char position (or first non-cite char nearby).
        let citeWord = -1;
        for (let k = startInBody + mm.index; k < Math.min(startInBody + mm.index + 30, charMap.length); k++) {
          if (charMap[k] >= 0) { citeWord = charMap[k]; break; }
        }
        // Or the previous word if no later word maps.
        if (citeWord < 0) {
          for (let k = startInBody + mm.index - 1; k >= Math.max(0, startInBody + mm.index - 30); k--) {
            if (charMap[k] >= 0) { citeWord = charMap[k]; break; }
          }
        }
        tokens.push({ kind: 'cite', citeIdx: idx, source: src, voiceWord: citeWord });
        lastIdx = mm.index + mm[0].length;
      }
      if (lastIdx < sent.length) {
        tokens.push(...wordsFromRun(sent.slice(lastIdx), startInBody + lastIdx, charMap));
      }
      paraSentences.push(tokens);
      totalTokens += tokens.length;
    });
    paragraphs.push(paraSentences);
    absoluteOffset += para.length + 2;
  });
  return { paragraphs, total: totalTokens };
}

function wordsFromRun(run, startInBody, charMap) {
  const out = [];
  if (!run) return out;
  // Match word-units (alnum + clinging punctuation) plus separator runs.
  const re = /\S+|\s+/g;
  let m;
  while ((m = re.exec(run)) !== null) {
    const piece = m[0];
    const offsetInRun = m.index;
    if (/^\s+$/.test(piece)) {
      out.push({ kind: 'space', text: piece });
      continue;
    }
    // Determine voice word: the first body-char in the piece that maps
    // to a voice word.
    let vw = -1;
    for (let k = 0; k < piece.length; k++) {
      const idx = startInBody + offsetInRun + k;
      if (idx < charMap.length && charMap[idx] >= 0) { vw = charMap[idx]; break; }
    }
    out.push({ kind: 'word', text: piece, voiceWord: vw });
  }
  return out;
}

export default function InlineCitations({
  body,
  sources = [],
  testIdPrefix = 'citation',
  // Word-level karaoke props (Apr 30 2026 v3)
  voiceText = '',
  wordRanges = [],
  voiceCharOffset = 0,
  activeWordIdx = -1,
  isPlaying = false,
  autoScroll = false,
  // Class for typography — bigger + brighter base now.
  className = 'font-historical text-xl md:text-2xl lg:text-2xl text-white/80 leading-relaxed md:leading-loose tracking-tight',
}) {
  const safeSources = Array.isArray(sources) ? sources : [];

  // Tokenize once per body/voice-data change.
  const { paragraphs, total } = useMemo(
    () => _tokenize(body || '', safeSources, voiceText, wordRanges, voiceCharOffset),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [body, voiceText, wordRanges, voiceCharOffset],
  );

  // Compute this body's voice-word range so we know if we're "active".
  const bodyWordRange = useMemo(() => {
    let firstVw = -1;
    let lastVw = -1;
    for (const sentences of paragraphs) {
      for (const tokens of sentences) {
        for (const t of tokens) {
          if (t.kind === 'word' && t.voiceWord >= 0) {
            if (firstVw < 0) firstVw = t.voiceWord;
            lastVw = Math.max(lastVw, t.voiceWord);
          }
          if (t.kind === 'cite' && t.voiceWord >= 0) {
            if (firstVw < 0) firstVw = t.voiceWord;
            lastVw = Math.max(lastVw, t.voiceWord);
          }
        }
      }
    }
    return { first: firstVw, last: lastVw };
  }, [paragraphs]);

  // Per-paragraph voice-word ranges. Apr 30 2026 iter_60 per Lead:
  // "It should only open the actual paragraph its speaking on and
  // the fade as it starts the next paragraph." We compute the first
  // and last voice-word index inside each <p>, then at render-time
  // check whether activeWordIdx falls inside that range. Only the
  // ONE paragraph containing the active word lights up; everyone
  // else fades to a dim baseline. CSS handles the cross-fade.
  const paragraphRanges = useMemo(() => {
    return paragraphs.map((sentences) => {
      let f = -1;
      let l = -1;
      for (const tokens of sentences) {
        for (const t of tokens) {
          if ((t.kind === 'word' || t.kind === 'cite') && t.voiceWord >= 0) {
            if (f < 0) f = t.voiceWord;
            l = Math.max(l, t.voiceWord);
          }
        }
      }
      return { first: f, last: l };
    });
  }, [paragraphs]);

  let phase = 'upcoming';
  if (bodyWordRange.first >= 0 && activeWordIdx >= 0) {
    if (activeWordIdx >= bodyWordRange.first && activeWordIdx <= bodyWordRange.last) phase = 'active';
    else if (activeWordIdx > bodyWordRange.last) phase = 'past';
  }

  // Auto-scroll REMOVED Apr 30 2026 per Lead — DO NOT re-add. Was
  // fighting the user's manual scroll (yanking the page back down every
  // time the user tried to scroll up to re-read an earlier sentence)
  // AND was landing the active word below the viewport fold, not at
  // the centerline. The user now scrolls freely; the word highlight
  // still lights up wherever they are on the page.
  const containerRef = useRef(null);

  // Fallback rendering when no alignment data is available — render plain
  // prose at the new brighter baseline so the page is never broken.
  if (!voiceText || !wordRanges.length) {
    return (
      <div className={className} data-testid={`${testIdPrefix}-body`} data-phase="no-alignment">
        {paragraphs.map((sentences, pIdx) => (
          <p key={`para-${pIdx}`} className="mb-5 last:mb-0">
            {sentences.map((tokens, sIdx) => (
              <span key={`s-${sIdx}`}>
                {tokens.map((t, ti) => {
                  if (t.kind === 'space') return <span key={ti}>{t.text}</span>;
                  if (t.kind === 'cite') {
                    return (
                      <CiteLink
                        key={ti}
                        source={t.source}
                        index={t.citeIdx}
                        isActiveWord={false}
                        testIdPrefix={testIdPrefix}
                        voiceWordIdx={t.voiceWord}
                      />
                    );
                  }
                  return <span key={ti}>{t.text}</span>;
                })}
                {sIdx < sentences.length - 1 ? ' ' : ''}
              </span>
            ))}
          </p>
        ))}
      </div>
    );
  }

  return (
    <div className={className} ref={containerRef} data-testid={`${testIdPrefix}-body`} data-phase={phase} data-token-count={total}>
      {paragraphs.map((sentences, pIdx) => {
        // Paragraph-level state drives brightness. Only the paragraph
        // containing the currently-narrated word is at full brightness;
        // all other paragraphs fade to a dim baseline. When audio is
        // NOT playing, every paragraph renders at full readable tone
        // so the page is never an unreadable blob — the script must be
        // readable even with autoplay blocked.
        const range = paragraphRanges[pIdx] || { first: -1, last: -1 };
        const hasActiveMarker = isPlaying && activeWordIdx >= 0 && range.first >= 0;
        const isActiveParagraph = hasActiveMarker
          && activeWordIdx >= range.first
          && activeWordIdx <= range.last;
        const paraState = !hasActiveMarker
          ? 'idle'
          : isActiveParagraph
            ? 'active'
            : activeWordIdx > range.last
              ? 'past'
              : 'upcoming';
        const paraClass = {
          idle:     'chain-para chain-para-idle',
          active:   'chain-para chain-para-active',
          past:     'chain-para chain-para-past',
          upcoming: 'chain-para chain-para-upcoming',
        }[paraState];
        return (
          <p
            key={`para-${pIdx}`}
            className={`mb-5 last:mb-0 ${paraClass}`}
            data-para-state={paraState}
            data-testid={`${testIdPrefix}-para-${pIdx}`}
          >
            {sentences.map((tokens, sIdx) => {
              return (
                <span key={`s-${sIdx}`}>
                  {tokens.map((t, ti) => {
                    if (t.kind === 'space') return <span key={ti}>{t.text}</span>;
                    if (t.kind === 'cite') {
                      return (
                        <CiteLink
                          key={ti}
                          source={t.source}
                          index={t.citeIdx}
                          isActiveWord={false}
                          testIdPrefix={testIdPrefix}
                          voiceWordIdx={t.voiceWord}
                        />
                      );
                    }
                    return (
                      <span
                        key={ti}
                        data-voice-word={t.voiceWord}
                      >
                        {t.text}
                      </span>
                    );
                  })}
                  {sIdx < sentences.length - 1 ? ' ' : ''}
                </span>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

export { _tokenize as __tokenizeForTests, _extractAuthorYear as __extractAuthorYearForTests };
